'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { createCareerOfferImpressionTracker } from '../model/impression';
import { CAREER_OFFER_EVENTS, type CareerOfferEvent } from '../model/events';
import { useCareerOfferAnalytics } from './use-career-offer-analytics';

export const useCareerOfferVisibilityOnce = (onVisible: () => void) => {
	const [element, setElement] = useState<HTMLElement | null>(null);
	const onVisibleRef = useRef(onVisible);
	onVisibleRef.current = onVisible;

	useEffect(() => {
		if (!element || !('IntersectionObserver' in window)) return;

		const tracker = createCareerOfferImpressionTracker({
			schedule: (callback) => window.setTimeout(callback, 1000),
			cancel: (timer) => window.clearTimeout(timer),
			emit: () => onVisibleRef.current(),
		});
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) tracker.update(entry);
			},
			{ threshold: [0.5] },
		);
		observer.observe(element);

		return () => {
			observer.disconnect();
			tracker.dispose();
		};
	}, [element]);

	return useCallback((node: HTMLElement | null) => setElement(node), []);
};

export const useCareerOfferPromoTracking = (
	context: Omit<CareerOfferEvent, 'name'>,
) => {
	const { trackEvent } = useCareerOfferAnalytics();
	const contextRef = useRef(context);
	contextRef.current = context;
	const ref = useCareerOfferVisibilityOnce(() =>
		trackEvent({
			name: CAREER_OFFER_EVENTS.PROMO_IMPRESSION,
			...contextRef.current,
		}),
	);
	const onClick = useCallback(
		() =>
			trackEvent({
				name: CAREER_OFFER_EVENTS.PROMO_CLICK,
				...contextRef.current,
			}),
		[trackEvent],
	);

	return { ref, onClick };
};
